'use client';
import { useId } from 'react';
import Script from 'next/script';
import { trackEvent } from 'lib/analytics';

export type HubSpotFormProps = {
  formId: string;
  portalId: string;
  formInstanceId: string;
  region?: string;
  pageName: string;
  hsFormKey: string;
  contentType: string;
  formsBaseUrl: string;
  cssClass?: string;
  trackingEventName: string;
  trackingLocation: string;
};

const attachFormTracking = (
  formElement: HTMLFormElement,
  trackingEventName: string,
  trackingLocation: string
) => {
  const clickedFields = new Set<string>();

  // Use event delegation on the main form element for focus/click events
  formElement.addEventListener('focusin', (event) => {
    const target = event.target as HTMLElement;
    if (target.matches('input:not([type="hidden"]), select, textarea')) {
      const field = target as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement;
      const fieldName = field.name || field.id || 'unknown_field';
      const uniqueId = `${formElement.id}-${fieldName}`;

      if (!clickedFields.has(uniqueId)) {
        trackEvent('input_field_click', {
          formName: trackingEventName,
          fieldName: fieldName,
          location: trackingLocation,
        });

        clickedFields.add(uniqueId);
      }
    }
  });

  // Attach submit attempt listener directly to the form element
  formElement.addEventListener('submit', () => {
    trackEvent('CTA_clicked', {
      selection: 'submit',
      location: 'body',
      path: '/contact',
      action: 'submit form',
    });
  });
};

const HubSpotForm = ({
  formId,
  portalId,
  formInstanceId,
  region = 'na1',
  pageName,
  hsFormKey,
  contentType,
  formsBaseUrl,
  cssClass,
  trackingEventName,
  trackingLocation,
}: HubSpotFormProps) => {
  const targetId = useId().replaceAll(':', '');

  if (!formId || !portalId) {
    return null;
  }

  return (
    <>
      <div className="hubspot-form" id={targetId}></div>
      <Script
        type="text/javascript"
        src="https://js.hsforms.net/forms/embed/v2.js"
        onReady={() => {
          const options = {
            portalId,
            formId,
            formInstanceId,
            region,
            pageName,
            hsFormKey,
            css: '',
            target: `#${targetId}`,
            contentType,
            formsBaseUrl,
            formData: cssClass ? { cssClass } : {},
            getExtraMetaDataBeforeSubmit: undefined as unknown,

            onFormReady: function () {
              // using the unique ID assigned to the parent DIV.
              setTimeout(() => {
                const containerElement = document.getElementById(targetId);
                let formElement: HTMLFormElement | null = null;

                if (containerElement) {
                  // Query the form directly inside the HubSpot wrapper
                  formElement = containerElement.querySelector('form');
                }

                if (formElement) {
                  attachFormTracking(
                    formElement,
                    trackingEventName,
                    trackingLocation
                  );
                } else {
                  console.error(
                    'HubSpot form element not found for analytics tracking.'
                  );
                }
              }, 0);
            },

            onFormSubmitted: function () {
              trackEvent(trackingEventName, {
                formName: trackingEventName,
                location: trackingLocation,
                status: 'success',
              });
            },
          };

          options.getExtraMetaDataBeforeSubmit = function () {
            const metadata: Record<string, unknown> = {};
            const windowWithHubSpot = window as unknown as {
              hbspt?: {
                targetedContentMetadata?: Array<[string, string, string]>;
              };
            };

            if (windowWithHubSpot.hbspt?.targetedContentMetadata) {
              const count =
                windowWithHubSpot.hbspt.targetedContentMetadata.length;
              const targetedContentData = [];
              for (let i = 0; i < count; i++) {
                const tc = windowWithHubSpot.hbspt.targetedContentMetadata[i];
                if (!tc || tc.length !== 3) {
                  continue;
                }
                targetedContentData.push({
                  definitionId: tc[0],
                  criterionId: tc[1],
                  smartTypeId: tc[2],
                });
              }
              metadata['targetedContentMetadata'] =
                JSON.stringify(targetedContentData);
            }

            return metadata;
          };

          const windowWithHubSpotForms = window as unknown as {
            hbspt?: {
              forms?: {
                create: (opts: unknown) => void;
              };
            };
          };

          windowWithHubSpotForms.hbspt?.forms?.create(options);
        }}
      />
    </>
  );
};

export default HubSpotForm;
